// Public form page — /f/{slug}, no auth. Honeypot + signed token anti-bot.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formsAPI } from '../lib/api';
import { Field, btnPrimary, inputCls } from '../components/ui';

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [website, setWebsite] = useState(''); // honeypot
  const [state, setState] = useState('form'); // form | sent | error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    formsAPI
      .publicGet(slug)
      .then(({ data }) => setForm(data))
      .catch(() => setState('error'));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await formsAPI.publicSubmit(slug, { values, website, form_token: form.form_token });
      setState('sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again.');
      // refresh the token (it may have expired)
      try {
        const { data } = await formsAPI.publicGet(slug);
        setForm((f) => ({ ...f, form_token: data.form_token }));
      } catch {
        /* keep old token */
      }
    } finally {
      setBusy(false);
    }
  };

  if (state === 'error') {
    return <div className="flex min-h-screen items-center justify-center text-sm text-pm-muted">This form is not available.</div>;
  }
  if (!form) return null;
  if (state === 'sent') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <div className="text-lg font-semibold">Thank you!</div>
          <p className="mt-1 text-sm text-pm-muted">Your submission was received.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <h1 className="text-xl font-semibold">{form.title}</h1>
      {form.description && <p className="mt-1 text-sm text-pm-muted">{form.description}</p>}
      <form onSubmit={submit} className="mt-6 space-y-4 rounded-lg border border-pm-border bg-pm-surface p-5">
        {form.fields.map((f) => (
          <Field key={f.id} label={`${f.label}${f.required ? ' *' : ''}`}>
            {f.type === 'textarea' ? (
              <textarea
                value={values[f.id] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                required={f.required}
                rows={4}
                className={inputCls}
              />
            ) : f.type === 'select' ? (
              <select
                value={values[f.id] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                required={f.required}
                className={inputCls}
              >
                <option value="">Choose…</option>
                {(f.options || []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.type === 'email' ? 'email' : f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                value={values[f.id] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                required={f.required}
                className={inputCls}
              />
            )}
          </Field>
        ))}
        {/* honeypot — hidden from humans */}
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
        />
        {error && (
          <p className="text-sm" style={{ color: 'var(--pm-danger)' }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className={`${btnPrimary} w-full`}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
